import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_DOES_NOT_QUALIFY,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_NOT_ASSESSED_ID,
  SITE_STATUS_SUPERSEDED_ID,
} from '../constants/siteStatuses'

const KBA_STATUSES_OPEN_SUPERSEDE_MODAL = [
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DELISTED_ID,
  SITE_STATUS_DOES_NOT_QUALIFY,
  SITE_STATUS_NOT_ASSESSED_ID,
]

/**
 * Sites management supersede dialog: KBA statuses that can invoke supersede (not undo).
 * Must stay aligned with secretariat menu default branch + {@link withSupersedeModalIntent}.
 * @param {object} site
 * @returns {boolean}
 */
export function isKbaStatusEligibleForSupersedeModal(site) {
  const id = site?.SiteStatus?.id
  return id != null && KBA_STATUSES_OPEN_SUPERSEDE_MODAL.includes(id)
}

/**
 * IBA layer status for a site row (list/search may use IbaStatus, IbaStatusHistory, or raw id).
 * @param {object} site
 * @returns {number|undefined}
 */
export function resolveIbaStatusIdForSiteRow(site) {
  return site.IbaStatus?.id ?? site.IbaStatusHistory?.id ?? site.iba_status_id
}

/**
 * Menu label: undo when either KBA or IBA is superseded; otherwise supersede.
 * @param {object} site
 * @returns {'revertToConfirmed'|'supersede'}
 */
export function getSupersedeOrUndoMenuTitle(site) {
  const kbaSuperseded = site.SiteStatus?.id === SITE_STATUS_SUPERSEDED_ID
  const ibaSuperseded = resolveIbaStatusIdForSiteRow(site) === SITE_STATUS_SUPERSEDED_ID
  return kbaSuperseded || ibaSuperseded ? 'revertToConfirmed' : 'supersede'
}

/**
 * Site payload for SitesManagement: which modal opens depends on KBA vs IBA superseded state.
 * @param {object} site
 * @param {object} [extra] — merged after site (e.g. `{ isApproved: true }` for delisted flow)
 * @returns {object}
 */
export function withSupersedeModalIntent(site, extra = {}) {
  return {
    ...site,
    ...extra,
    revertSupersededModal: getSupersedeOrUndoMenuTitle(site) === 'revertToConfirmed',
  }
}

/**
 * Remove supersede/revert row when KBA status is below confirmed (draft, nominated, etc.).
 * @param {Array<{ title: string }>} items
 * @param {object} site
 */
export function removeSupersedeIfKbaStatusTooLow(items, site) {
  if (site.SiteStatus?.id != null && site.SiteStatus.id < SITE_STATUS_CONFIRMED_ID) {
    const idx = items.findIndex(
      (item) => item.title === 'supersede' || item.title === 'revertToConfirmed'
    )
    if (idx !== -1) items.splice(idx, 1)
  }
}

/**
 * Remove supersede/revert for edit-only KBA rows.
 * @param {Array<{ title: string }>} items
 * @param {object} site
 */
export function removeSupersedeIfKbaEditOnly(items, site) {
  if (site.SiteStatus?.id === SITE_STATUS_EDIT_ONLY_ID) {
    const idx = items.findIndex(
      (item) => item.title === 'supersede' || item.title === 'revertToConfirmed'
    )
    if (idx !== -1) items.splice(idx, 1)
  }
}
